export type LeadStatus =
  | "Initial"
  | "Pending"
  | "Success"
  | "Failure"
  | "DuplicateIP"
  | "Not_Supported";

export interface Lead {
  id: number;
  name: string;
  email: string;
  phone: string;
  websiteSharedUid: string;
  brandUid: string;
  brand: string;
  status: LeadStatus;
  industry: string;
  loanPurpose: string;
  amount: string;
  revenue: string;
  credit: string;
  timeInBusiness: string;
  zip: string;
  businessName: string;
  userUniqueId: string;
  trafficSource: string;
  adId: string;
  adPlatformClickId: string;
  date: string;
  reportedAt: string | null;
  created: string;
  modified: string;
  // --- Scoring / brand-outcome fields: all sourced from the
  // brand_leads_report/ response (see "Extending POST .../brand_leads_report/
  // Response" spec). Nullable rather than optional — the API always returns
  // the key, null when not yet applicable (e.g. scoring not run yet).
  qualifiedByBrand: boolean | null;
  incomeFromBrand: number | null;
  brandLeadStatus: string | null;
  questionnaireFillTimeSeconds: number | null;
  leadScore: number | null;
  scoreRationale: string | null;
  approvalLikelihood: number | null;
  approvalRationale: string | null;
  scoringStatus: ScoringStatus | null;
  scoringUpdatedAt: string | null;
  ipAddress: string | null;
  duplicateIpLeads: DuplicateIpLead[];
  reportLog: ReportLogEntry[];
}

export interface DuplicateIpLead {
  id: number;
  fullName: string;
  brandName: string;
  created: string;
}

export type ScoringStatus = "completed" | "processing" | "failed";

export interface ReportLogEntry {
  id: number;
  attempt: number;
  // The API's brand_leads_report_log rows carry no status field, only a raw,
  // brand-specific response_payload — "status" here is a best-effort
  // inference (see inferReportLogStatus), not a value returned by the API.
  status: "success" | "failure" | "logged";
  createdAt: string;
  payload: string;
}

// The Leads Management screen is currently scoped to this single site. Its
// website_shared_uid differs per environment (staging/production don't share
// a database), but the site_name is stable across environments, so access
// and the shared_uid to query with are both resolved from it at runtime.
export const LEADS_MANAGEMENT_SITE_NAME = "Opti Fund";

export const STATUS_ORDER: LeadStatus[] = [
  "Pending",
  "Initial",
  "Success",
  "Failure",
  "DuplicateIP",
  "Not_Supported",
];

export const STATUS_META: Record<
  LeadStatus,
  { label: string; color: string; bg: string }
> = {
  Initial: { label: "Initial", color: "#1565C0", bg: "#E3F2FD" },
  Pending: { label: "Pending", color: "#E65100", bg: "#FFF3E0" },
  Success: { label: "Success", color: "#2E7D32", bg: "#E8F5E9" },
  Failure: { label: "Failure", color: "#C62828", bg: "#FFEBEE" },
  DuplicateIP: { label: "Duplicate IP", color: "#AD1457", bg: "#FCE4EC" },
  Not_Supported: { label: "Not Supported", color: "#616161", bg: "#F0F0F0" },
};

export const BRAND_COLOR = "#8E24AA";

// DuplicateIP leads are re-reportable just like Pending — the API only
// blocked the automatic report, it didn't reject the lead outright, so the
// user can still choose to report it manually.
export function isReportableStatus(status: LeadStatus): boolean {
  return status === "Pending" || status === "DuplicateIP";
}

export interface ScoreMeta {
  color: string;
  bg: string;
  band: string;
}

export function getScoreMeta(score: number): ScoreMeta {
  if (score >= 7) return { color: "#2E7D32", bg: "#E8F5E9", band: "Strong" };
  if (score >= 4) return { color: "#EF6C00", bg: "#FFF3E0", band: "Moderate" };
  return { color: "#C62828", bg: "#FFEBEE", band: "Weak" };
}

export function getQualifiedMeta(
  qualified: boolean | null,
): { label: string; color: string; bg: string } {
  if (qualified === true) return { label: "Qualified", color: "#2E7D32", bg: "#E8F5E9" };
  if (qualified === false) return { label: "Not Qualified", color: "#C62828", bg: "#FFEBEE" };
  return { label: "Pending", color: "#9e9e9e", bg: "#F0F0F0" };
}

// No "Unknown"/null-only option — the server's qualified_by_brand param can
// only express true/false/omitted ("null leads excluded from both"), it
// can't filter for "qualification pending" specifically.
export const QUALIFIED_FILTER_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

// Fixed threshold set per the lead_scoring_min request param spec.
export const MIN_SCORE_FILTER_OPTIONS = [
  { value: "0", label: "Any" },
  { value: "4", label: "4.0+" },
  { value: "6", label: "6.0+" },
  { value: "7", label: "7.0+" },
  { value: "8.5", label: "8.5+" },
];

// has_duplicate_ip is a boolean on the server (shared vs unique), not a
// specific IP value — "view leads from this exact IP" now goes through the
// general search param instead (see goToFilteredLeads' search override).
export const IP_FILTER_OPTIONS = [
  { value: "All", label: "Any IP" },
  { value: "Shared", label: "Shared IP only" },
  { value: "Unique", label: "Unique IP only" },
];

export const SCORING_STATUS_META: Record<
  ScoringStatus,
  { label: string; color: string; bg: string }
> = {
  completed: { label: "Completed", color: "#2E7D32", bg: "#E8F5E9" },
  processing: { label: "Processing", color: "#EF6C00", bg: "#FFF3E0" },
  failed: { label: "Failed", color: "#C62828", bg: "#FFEBEE" },
};

export const REPORT_LOG_STATUS_META: Record<
  ReportLogEntry["status"],
  { label: string; color: string; bg: string }
> = {
  success: { label: "Success", color: "#2E7D32", bg: "#E8F5E9" },
  failure: { label: "Failure", color: "#C62828", bg: "#FFEBEE" },
  logged: { label: "Logged", color: "#616161", bg: "#F0F0F0" },
};

/**
 * The brand_leads_report_log rows returned by the API have no explicit
 * status field — response_payload is a raw, brand-specific blob. This infers
 * a status for display: a common `success`/`status` key inside the payload
 * if present, otherwise the most recent attempt inherits the lead's own
 * report_status, otherwise a neutral "logged" fallback. Best-effort by
 * design — may not be accurate for every brand's payload shape.
 */
function inferReportLogStatus(
  payload: any,
  isLastEntry: boolean,
  leadStatus: LeadStatus,
): ReportLogEntry["status"] {
  if (payload && typeof payload === "object") {
    if (typeof payload.success === "boolean") return payload.success ? "success" : "failure";
    if (typeof payload.status === "string") {
      const normalized = payload.status.toLowerCase();
      if (["success", "accepted", "ok", "approved"].includes(normalized)) return "success";
      if (["failed", "failure", "error", "declined", "rejected"].includes(normalized)) return "failure";
    }
  }
  if (isLastEntry) {
    if (leadStatus === "Success") return "success";
    if (leadStatus === "Failure") return "failure";
  }
  return "logged";
}

export function formatLeadDateTime(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  let hours = date.getHours();
  const meridiem = hours < 12 ? "a.m." : "p.m.";
  hours = hours % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}, ${hours}:${minutes} ${meridiem}`;
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
}

function toIsoDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Leads Management defaults to the last 30 days. */
export function getDefaultDateRange(referenceDate: Date = new Date()): {
  from: string;
  to: string;
} {
  return {
    from: toIsoDateString(new Date(referenceDate.getTime() - 30 * 86400000)),
    to: toIsoDateString(referenceDate),
  };
}

export const MAX_DATE_RANGE_DAYS = 365;

/** The latest end date the picker should allow for a given start date, in yyyy-mm-dd form. */
export function getMaxEndDate(start: string): string | undefined {
  if (!start) return undefined;
  const startDate = new Date(`${start}T00:00:00`);
  if (Number.isNaN(startDate.getTime())) return undefined;
  return toIsoDateString(new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 86400000));
}

/** The earliest start date the picker should allow for a given end date, in yyyy-mm-dd form. */
export function getMinStartDate(end: string): string | undefined {
  if (!end) return undefined;
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(endDate.getTime())) return undefined;
  return toIsoDateString(new Date(endDate.getTime() - MAX_DATE_RANGE_DAYS * 86400000));
}

/** True when end is before start, or more than MAX_DATE_RANGE_DAYS after it (both set). */
export function isDateRangeInvalid(start: string, end: string): boolean {
  if (!start || !end) return false;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;
  const daysBetween = (endDate.getTime() - startDate.getTime()) / 86400000;
  return daysBetween < 0 || daysBetween > MAX_DATE_RANGE_DAYS;
}

// DRF's DecimalField serializes to a string by default (avoids float rounding)
// rather than the doc's stated "Decimal" — income_from_brand has been observed
// coming back as e.g. "100.00" instead of 100. Coerce defensively so
// formatCurrency/getScoreMeta/arithmetic always get a real number.
function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
}

/** Maps a single record from POST /partners/brand_leads_report/ into a Lead. */
export function mapApiLeadToLead(record: any): Lead {
  const reportLogRaw: any[] = Array.isArray(record.brand_leads_report_log)
    ? record.brand_leads_report_log
    : [];
  const sortedLog = [...reportLogRaw].sort(
    (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
  );

  return {
    id: record.id,
    name: record.full_name,
    email: record.email,
    phone: record.phone,
    websiteSharedUid: record.website_shared_uid,
    brandUid: record.brand_uid,
    brand: record.brand_name,
    status: record.report_status,
    industry: record.business_industry,
    loanPurpose: record.loan_purpose,
    amount: record.borrow_amount,
    revenue: record.monthly_revenue,
    credit: record.credit_rating,
    timeInBusiness: record.time_in_business,
    zip: record.zip_code,
    businessName: record.business_name,
    userUniqueId: record.user_unique_id,
    trafficSource: record.traffic_source,
    adId: record.adid,
    adPlatformClickId: record.ad_platform_click_id,
    date: record.date_of_acquisition,
    reportedAt: record.reported_at,
    created: record.created,
    modified: record.modified,
    qualifiedByBrand: record.qualified_by_brand,
    incomeFromBrand: toNumberOrNull(record.income_from_brand),
    brandLeadStatus: record.brand_lead_status,
    questionnaireFillTimeSeconds: record.questionnaire_fill_time,
    leadScore: toNumberOrNull(record.lead_scoring),
    scoreRationale: record.lead_scoring_description,
    approvalLikelihood: toNumberOrNull(record.loan_approval_likelihood),
    approvalRationale: record.loan_approval_likelihood_description,
    scoringStatus: record.lead_scoring_status,
    scoringUpdatedAt: record.lead_scoring_updated_at,
    ipAddress: record.ip_address,
    duplicateIpLeads: (record.duplicate_ip_leads ?? []).map((dup: any) => ({
      id: dup.id,
      fullName: dup.full_name,
      brandName: dup.brand_name,
      created: dup.created,
    })),
    reportLog: sortedLog.map((entry, index) => ({
      id: entry.id,
      attempt: index + 1,
      status: inferReportLogStatus(
        entry.response_payload,
        index === sortedLog.length - 1,
        record.report_status,
      ),
      createdAt: entry.created,
      payload:
        typeof entry.response_payload === "string"
          ? entry.response_payload
          : JSON.stringify(entry.response_payload, null, 2),
    })),
  };
}

export interface LeadsAggregations {
  byStatus: Array<{ status: LeadStatus; count: number }>;
  byDay: Array<{ date: string; count: number }>;
  byBrand: Array<{ brandUid: string; brandName: string; count: number }>;
  qualifiedByBrand: { count: number; successLeadsCount: number; percentOfSuccessLeads: number | null };
  incomeFromBrand: { total: number; successLeadsCount: number; avgPerSuccessLead: number | null };
  avgLeadScore: { average: number | null; scoredLeadsCount: number };
  avgFillTimeSeconds: { averageSeconds: number | null; leadsWithValueCount: number };
  leadScoreDistribution: Array<{ range: string; count: number }>;
  incomeByBrand: { total: number; byBrand: Array<{ brandName: string; amount: number }> };
}

export const EMPTY_AGGREGATIONS: LeadsAggregations = {
  byStatus: [],
  byDay: [],
  byBrand: [],
  qualifiedByBrand: { count: 0, successLeadsCount: 0, percentOfSuccessLeads: null },
  incomeFromBrand: { total: 0, successLeadsCount: 0, avgPerSuccessLead: null },
  avgLeadScore: { average: null, scoredLeadsCount: 0 },
  avgFillTimeSeconds: { averageSeconds: null, leadsWithValueCount: 0 },
  leadScoreDistribution: [],
  incomeByBrand: { total: 0, byBrand: [] },
};

/** Maps the `aggregations` object from POST /partners/brand_leads_report/. */
export function mapApiAggregations(aggregations: any): LeadsAggregations {
  return {
    byStatus: (aggregations?.by_status ?? []).map((item: any) => ({
      status: item.report_status,
      count: toNumberOrNull(item.count) ?? 0,
    })),
    byDay: (aggregations?.by_day ?? []).map((item: any) => ({
      date: item.date,
      count: toNumberOrNull(item.count) ?? 0,
    })),
    byBrand: (aggregations?.by_brand ?? []).map((item: any) => ({
      brandUid: item.brand_uid,
      brandName: item.brand_name,
      count: toNumberOrNull(item.count) ?? 0,
    })),
    qualifiedByBrand: {
      count: toNumberOrNull(aggregations?.qualified_by_brand?.count) ?? 0,
      successLeadsCount: toNumberOrNull(aggregations?.qualified_by_brand?.success_leads_count) ?? 0,
      percentOfSuccessLeads: toNumberOrNull(aggregations?.qualified_by_brand?.percent_of_success_leads),
    },
    incomeFromBrand: {
      total: toNumberOrNull(aggregations?.income_from_brand?.total) ?? 0,
      successLeadsCount: toNumberOrNull(aggregations?.income_from_brand?.success_leads_count) ?? 0,
      avgPerSuccessLead: toNumberOrNull(aggregations?.income_from_brand?.avg_per_success_lead),
    },
    avgLeadScore: {
      average: toNumberOrNull(aggregations?.avg_lead_score?.average),
      scoredLeadsCount: toNumberOrNull(aggregations?.avg_lead_score?.scored_leads_count) ?? 0,
    },
    avgFillTimeSeconds: {
      averageSeconds: toNumberOrNull(aggregations?.avg_fill_time_seconds?.average_seconds),
      leadsWithValueCount: toNumberOrNull(aggregations?.avg_fill_time_seconds?.leads_with_value_count) ?? 0,
    },
    leadScoreDistribution: (aggregations?.lead_score_distribution ?? []).map((item: any) => ({
      range: item.range,
      count: toNumberOrNull(item.count) ?? 0,
    })),
    incomeByBrand: {
      total: toNumberOrNull(aggregations?.income_by_brand?.total) ?? 0,
      byBrand: (aggregations?.income_by_brand?.by_brand ?? []).map((item: any) => ({
        brandName: item.brand_name,
        amount: toNumberOrNull(item.amount) ?? 0,
      })),
    },
  };
}

/**
 * Checks whether the logged-in user (from /users/me/, stored as selectUserData)
 * has access to the site with the given site_name. Mirrors the lookup in
 * src/customHooks/useWebsiteSharedUid.ts.
 */
export function userHasWebsiteAccess(
  userData: any,
  siteName: string,
): boolean {
  const websitePermissions = userData?.data?.website_permissions || [];
  return websitePermissions.some((permission: any) => {
    const details: any = Object.values(permission)[0];
    return details?.site_name === siteName;
  });
}
